import {
  connectGradeOrder,
  connectGrades,
  GradeKey,
  sortGradesByOrder,
} from "@nimble/datamodel/enums/connectGrades";
import {
  connectSubjects,
  SubjectKey,
} from "@nimble/datamodel/enums/connectSubjects";
import { convertPreferencesToFilters as convertPreferencesToFiltersCore } from "@nimble/datamodel/mappings/preferencesToFilters";
import plur from "plur";

import { FilterValues } from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/filter-schema";
import { DEFAULT_MILE_RANGE } from "@/lib/constants";
import type { UserDiscoverInfo } from "@/lib/types/user-discover-info";

type DiscoverPreference = NonNullable<UserDiscoverInfo["preference"]>;

function gradeLabel(key: GradeKey): string {
  return connectGrades[key].label;
}

function subjectLabel(key: SubjectKey): string {
  return connectSubjects[key].label;
}

function summarizeSubjects(subjects: readonly string[]): string | null {
  const known = subjects.filter((s): s is SubjectKey => s in connectSubjects);
  const [first] = known;
  if (!first) return null;

  if (known.length === 1) return subjectLabel(first);
  return `${subjectLabel(first)} + ${known.length - 1}`;
}

function summarizeLocation(filters: FilterValues): string | null {
  if (filters.address) {
    if (filters.radius) {
      return `${filters.radius} mi from ${filters.address}`;
    }
    return filters.address;
  }
  return null;
}

function summarizeGrades(grades: readonly string[]): string | null {
  const sorted = sortGradesByOrder(grades);
  const head = sorted.at(0);
  const tail = sorted.at(-1);
  if (!head || !tail) return null;

  const order = (g: GradeKey) => connectGradeOrder.indexOf(g);
  if (sorted.length === 1) return `Grade ${gradeLabel(head)}`;

  if (order(tail) - order(head) === sorted.length - 1) {
    return `Grades ${gradeLabel(head)}–${gradeLabel(tail)}`;
  }
  return `${sorted.length} grades`;
}

function countLabel(items: readonly unknown[], noun: string): string | null {
  if (items.length === 0) return null;
  return `${items.length} ${plur(noun, items.length)}`;
}

function summarizeFlags(filters: FilterValues): string[] {
  const flags: string[] = [];
  if (filters.quickApply) flags.push("Quick Apply");
  if (filters.savedSchools) flags.push("Saved schools only");
  return flags;
}

export const summarizeFilters = (filters: FilterValues): string => {
  const parts: string[] = [];

  const subjects = summarizeSubjects(filters.subjects ?? []);
  if (subjects) parts.push(subjects);

  const grades = summarizeGrades(filters.grades ?? []);
  if (grades) parts.push(grades);

  const districts = countLabel(filters.districts ?? [], "district");
  if (districts) parts.push(districts);

  const schools = countLabel(filters.schools ?? [], "school");
  if (schools) parts.push(schools);

  const location = summarizeLocation(filters);
  if (location) parts.push(location);

  parts.push(...summarizeFlags(filters));

  if (parts.length === 0) return "All jobs";
  return parts.join(" · ");
};

function normalizeForMatch(filters: FilterValues): string {
  const sorted = (values?: readonly string[]) => [...(values ?? [])].sort();
  return JSON.stringify({
    address: (filters.address ?? "").trim(),
    radius: filters.radius ?? DEFAULT_MILE_RANGE,
    savedSchools: filters.savedSchools ?? false,
    showSchoolsWithNoRoles: filters.showSchoolsWithNoRoles ?? false,
    quickApply: filters.quickApply ?? false,
    districts: sorted(filters.districts),
    schools: sorted(filters.schools),
    grades: sorted(filters.grades),
    subjects: sorted(filters.subjects),
  });
}

export function filtersMatch(a: FilterValues, b: FilterValues): boolean {
  return normalizeForMatch(a) === normalizeForMatch(b);
}

export function hasMeaningfulPreferences(filters: FilterValues): boolean {
  return Boolean(
    filters.subjects?.length ||
      filters.grades?.length ||
      filters.address?.trim(),
  );
}

export function resolveSearchAddress(
  address: string | undefined,
  defaultAddress: string,
): string {
  return address?.trim() || defaultAddress;
}

export { toDiscoverBoardFilters as toFilterValues } from "@nimble/candidate-api/discover-board-filters";

export function convertPreferencesToFilters(
  preference: DiscoverPreference,
): FilterValues {
  const filters = convertPreferencesToFiltersCore({
    grades: preference.grades ?? [],
    subjects: preference.categories_connect ?? [],
    location: preference.location ?? "",
    milesWithin: preference.miles_within,
  });

  return {
    ...filters,
    districts: [],
    schools: [],
    showSchoolsWithNoRoles: false,
    quickApply: false,
    savedSchools: false,
  };
}
